import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios, { AxiosResponse } from 'axios';
import { generateHeaders } from 'utils/APIUtils';
import { RootState } from 'store';
import { Reject } from '../Reject';
import querystring from 'querystring';
import { logout } from './auth';
import SpotSummary from 'domain/SpotSummary';
import { Comment, Spot, SpotShort } from 'domain/Spot';
import { submitNewSpot } from './newSpot';
import { handleApiError } from '../handleApiError';

interface SpotsState {
    gettingSpots: boolean;
    getSpotsError: boolean;
    getSpotsSuccess: boolean;
    spots?: SpotShort[];
    spotsSummary?: SpotSummary[];
    openingSpotDetail: boolean;
    spotDetailError: boolean;
    spotDetailSuccess: boolean;
    spotDetailData: Spot;
    swLat?: number;
    swLng?: number;
    neLat?: number;
    neLng?: number;
    zoom?: number;
    submittingNewSpot: boolean;
    submitNewSpotError: boolean;
    submitNewSpotSuccess: boolean;
    submitNewSpotErrorMessage: string;
    comments: Comment[];
    commentsNextCursor?: string;
    loadingComments: boolean;
    submittingComment: boolean;
}

const initialState: SpotsState = {
    gettingSpots: false,
    getSpotsError: false,
    getSpotsSuccess: true,
    spots: undefined,
    spotsSummary: undefined,
    openingSpotDetail: false,
    spotDetailError: false,
    spotDetailSuccess: false,
    spotDetailData: undefined,
    swLat: undefined,
    swLng: undefined,
    neLat: undefined,
    neLng: undefined,
    zoom: undefined,
    submittingNewSpot: false,
    submitNewSpotError: false,
    submitNewSpotSuccess: false,
    submitNewSpotErrorMessage: undefined,
    comments: [],
    commentsNextCursor: undefined,
    loadingComments: false,
    submittingComment: false,
};

type OpenSpotDetailResponse = Spot

interface GetSpotParams {
    swLat: number;
    swLng: number;
    neLat: number;
    neLng: number;
    zoom: number;
}

interface OpenSpotDetailParams {
    idSpot: string;
}

interface GetSpotsResponseObj {
    spots: SpotShort[];
    spotsSummary?: SpotSummary[];
    swLat?: number;
    swLng?: number;
    neLat?: number;
    neLng?: number;
    zoom?: number;
}

export const getSpots = createAsyncThunk<GetSpotsResponseObj, GetSpotParams, Reject>(
    'getSpots',
    async (params, thunkAPI) => {
        const { swLat, swLng, neLat, neLng, zoom } = params;
        console.log(`spot: swLat: ${swLat} - swLng: ${swLng} - neLat: ${neLat} - neLng: ${neLng}`)
        const { auth } = thunkAPI.getState() as RootState;
        const headers = generateHeaders(auth.jwt);
        try {
            const response: AxiosResponse<{ spots: SpotShort[] }> = await axios.get(
                `${process.env.REACT_APP_API_URL}/spot?${querystring.stringify({
                    swLat,
                    swLng,
                    neLat,
                    neLng,
                    zoom,
                })}`,
                { headers }
            );

            return {
                ...response.data,
                swLat,
                swLng,
                neLat,
                neLng,
                zoom,
            };
        } catch (error) {
            const apiError = handleApiError(error);
            return thunkAPI.rejectWithValue(apiError);
        }
    });

export const openSpotDetail = createAsyncThunk<OpenSpotDetailResponse, OpenSpotDetailParams, Reject>(
    'openSpotDetail',
    async (params, thunkAPI) => {
        const { auth } = thunkAPI.getState() as RootState;
        const headers = generateHeaders(auth.jwt);
        return axios
            .get(`${process.env.REACT_APP_API_URL}/spot/${params.idSpot}`, { headers })
            .then((response: AxiosResponse<Spot>) => response.data)
            .catch((error) => {
                const apiError = handleApiError(error);
                return thunkAPI.rejectWithValue(apiError);
            });
    }
);

interface SpotSocialParams { idSpot: string; }
interface CommentParams { idSpot: string; cursor?: string; }
interface AddCommentParams { idSpot: string; text: string; }
interface DeleteCommentParams { idSpot: string; commentId: string; }
interface CommentsResponse { comments: Comment[]; nextCursor?: string; }

export const likeSpot = createAsyncThunk<{ likeCount: number }, SpotSocialParams, Reject>(
    'likeSpot',
    async ({ idSpot }, thunkAPI) => {
        const { auth } = thunkAPI.getState() as RootState;
        return axios.post(`${process.env.REACT_APP_API_URL}/spot/${idSpot}/like`, {}, { headers: generateHeaders(auth.jwt) })
            .then((r: AxiosResponse<{ likeCount: number }>) => r.data)
            .catch((e) => thunkAPI.rejectWithValue(handleApiError(e)));
    }
);

export const unlikeSpot = createAsyncThunk<{ likeCount: number }, SpotSocialParams, Reject>(
    'unlikeSpot',
    async ({ idSpot }, thunkAPI) => {
        const { auth } = thunkAPI.getState() as RootState;
        return axios.delete(`${process.env.REACT_APP_API_URL}/spot/${idSpot}/like`, { headers: generateHeaders(auth.jwt) })
            .then((r: AxiosResponse<{ likeCount: number }>) => r.data)
            .catch((e) => thunkAPI.rejectWithValue(handleApiError(e)));
    }
);

export const checkinSpot = createAsyncThunk<{ checkinCount: number }, SpotSocialParams, Reject>(
    'checkinSpot',
    async ({ idSpot }, thunkAPI) => {
        const { auth } = thunkAPI.getState() as RootState;
        return axios.post(`${process.env.REACT_APP_API_URL}/spot/${idSpot}/checkin`, {}, { headers: generateHeaders(auth.jwt) })
            .then((r: AxiosResponse<{ checkinCount: number }>) => r.data)
            .catch((e) => thunkAPI.rejectWithValue(handleApiError(e)));
    }
);

export const removeCheckinSpot = createAsyncThunk<{ checkinCount: number }, SpotSocialParams, Reject>(
    'removeCheckinSpot',
    async ({ idSpot }, thunkAPI) => {
        const { auth } = thunkAPI.getState() as RootState;
        return axios.delete(`${process.env.REACT_APP_API_URL}/spot/${idSpot}/checkin`, { headers: generateHeaders(auth.jwt) })
            .then((r: AxiosResponse<{ checkinCount: number }>) => r.data)
            .catch((e) => thunkAPI.rejectWithValue(handleApiError(e)));
    }
);

export const getSpotComments = createAsyncThunk<CommentsResponse, CommentParams, Reject>(
    'getSpotComments',
    async ({ idSpot, cursor }, thunkAPI) => {
        const url = `${process.env.REACT_APP_API_URL}/spot/${idSpot}/comments${cursor ? `?cursor=${cursor}` : ''}`;
        return axios.get(url)
            .then((r: AxiosResponse<CommentsResponse>) => r.data)
            .catch((e) => thunkAPI.rejectWithValue(handleApiError(e)));
    }
);

export const addSpotComment = createAsyncThunk<Comment, AddCommentParams, Reject>(
    'addSpotComment',
    async ({ idSpot, text }, thunkAPI) => {
        const { auth } = thunkAPI.getState() as RootState;
        return axios.post(`${process.env.REACT_APP_API_URL}/spot/${idSpot}/comment`, { text }, { headers: generateHeaders(auth.jwt) })
            .then((r: AxiosResponse<Comment>) => r.data)
            .catch((e) => thunkAPI.rejectWithValue(handleApiError(e)));
    }
);

export const deleteSpotComment = createAsyncThunk<{ commentId: string }, DeleteCommentParams, Reject>(
    'deleteSpotComment',
    async ({ idSpot, commentId }, thunkAPI) => {
        const { auth } = thunkAPI.getState() as RootState;
        return axios.delete(`${process.env.REACT_APP_API_URL}/spot/${idSpot}/comment/${commentId}`, { headers: generateHeaders(auth.jwt) })
            .then(() => ({ commentId }))
            .catch((e) => thunkAPI.rejectWithValue(handleApiError(e)));
    }
);

const spotsSlice = createSlice({
    name: 'spots',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getSpots.pending, (state) => {
                state.gettingSpots = true;
                state.getSpotsError = false;
                state.getSpotsSuccess = false;
                state.spots = [];
                state.spotsSummary = [];
            })
            .addCase(getSpots.rejected, (state) => {
                state.gettingSpots = false;
                state.getSpotsError = true;
                state.getSpotsSuccess = false;
            })
            .addCase(getSpots.fulfilled, (state, action) => {
                state.gettingSpots = false;
                state.getSpotsError = false;
                state.getSpotsSuccess = true;
                state.spots = action.payload.spots;
                state.spotsSummary = action.payload.spotsSummary;
                state.neLat = action.payload.neLat;
                state.swLat = action.payload.swLat;
                state.neLng = action.payload.neLng;
                state.swLng = action.payload.swLng;
                state.zoom = action.payload.zoom;
            })
            .addCase(openSpotDetail.pending, (state) => {
                state.openingSpotDetail = true;
                state.spotDetailError = false;
                state.spotDetailSuccess = false;
                state.spotDetailData = undefined;
                state.comments = [];
                state.commentsNextCursor = undefined;
            })
            .addCase(openSpotDetail.rejected, (state) => {
                state.openingSpotDetail = false;
                state.spotDetailError = true;
                state.spotDetailSuccess = false;
                state.spotDetailData = undefined;
            })
            .addCase(openSpotDetail.fulfilled, (state, action) => {
                state.openingSpotDetail = false;
                state.spotDetailError = false;
                state.spotDetailSuccess = true;
                state.spotDetailData = action.payload;
            })
            .addCase(submitNewSpot.pending, (state) => {
                state.submittingNewSpot = true;
                state.submitNewSpotError = false;
                state.submitNewSpotSuccess = false;
            })
            .addCase(submitNewSpot.rejected, (state, action) => {
                state.submittingNewSpot = false;
                state.submitNewSpotError = true;
                state.submitNewSpotSuccess = false;
                state.submitNewSpotErrorMessage = action.error.message || 'Failed to submit new spot.';
            })
            .addCase(submitNewSpot.fulfilled, (state, action) => {
                state.submittingNewSpot = false;
                state.submitNewSpotError = false;
                state.submitNewSpotSuccess = true;
                state.spots.push(action.payload);
            })
            .addCase(likeSpot.fulfilled, (state, action) => {
                if (state.spotDetailData) {
                    state.spotDetailData.likeCount = action.payload.likeCount;
                    state.spotDetailData.likedByCurrentUser = true;
                }
            })
            .addCase(unlikeSpot.fulfilled, (state, action) => {
                if (state.spotDetailData) {
                    state.spotDetailData.likeCount = action.payload.likeCount;
                    state.spotDetailData.likedByCurrentUser = false;
                }
            })
            .addCase(checkinSpot.fulfilled, (state, action) => {
                if (state.spotDetailData) {
                    state.spotDetailData.checkinCount = action.payload.checkinCount;
                    state.spotDetailData.checkedInByCurrentUser = true;
                }
            })
            .addCase(removeCheckinSpot.fulfilled, (state, action) => {
                if (state.spotDetailData) {
                    state.spotDetailData.checkinCount = action.payload.checkinCount;
                    state.spotDetailData.checkedInByCurrentUser = false;
                }
            })
            .addCase(getSpotComments.pending, (state) => {
                state.loadingComments = true;
            })
            .addCase(getSpotComments.fulfilled, (state, action: PayloadAction<CommentsResponse>) => {
                state.loadingComments = false;
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                state.comments = [...state.comments, ...(action.payload.comments)];
                state.commentsNextCursor = action.payload.nextCursor;
            })
            .addCase(getSpotComments.rejected, (state) => {
                state.loadingComments = false;
            })
            .addCase(addSpotComment.pending, (state) => {
                state.submittingComment = true;
            })
            .addCase(addSpotComment.fulfilled, (state, action: PayloadAction<Comment>) => {
                state.submittingComment = false;
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                state.comments.push(action.payload);
                if (state.spotDetailData) {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    state.spotDetailData.commentCount = (state.spotDetailData.commentCount ?? 0) + 1;
                }
            })
            .addCase(addSpotComment.rejected, (state) => {
                state.submittingComment = false;
            })
            .addCase(deleteSpotComment.fulfilled, (state, action: PayloadAction<{ commentId: string }>) => {
                const deletedId = (action.payload as { commentId: string }).commentId;
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                state.comments = state.comments.filter((c) => c.commentId !== deletedId);
                if (state.spotDetailData) {
                    state.spotDetailData.commentCount = Math.max(0, (state.spotDetailData.commentCount ?? 1) - 1);
                }
            })

            .addCase(logout.toString(), () => ({ ...initialState }));
    },
});

export default spotsSlice.reducer;
